import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { BIO_MAX_VISIBLE_CHARS, bioVisibleTextLength, EImageVariant } from '@onesource/schemas';
import { FC } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { MdDownload, MdHelpOutline } from 'react-icons/md';

import { FILE_FORMATS } from '../file-uploader/file-uploader.constants';
import { OptionalChip, RequiredChip } from '../form-elements/form-label-chips';
import { buildProcessHandler } from '../media-manager/process-handler';
import { RhfMediaInlineUpload, RHFProfessionAutocomplete, RHFTextField } from '../rhf-inputs';
import { BIO_EXTENSIONS, type FormatFlagKey, RichHtmlEditor } from '../rich-html-editor';
import { isOtherProfessionSelected, PROFESSION_MAX, TProFormValues } from './steps';

const BIO_FORMATS: FormatFlagKey[] = [
  'bold',
  'italic',
  'underline',
  'bulletList',
  'orderedList',
  'subscript',
  'superscript',
];

const casualImageProcessHandler = buildProcessHandler([EImageVariant.w400]);
const landscapeImageProcessHandler = buildProcessHandler([EImageVariant.w1375]);

type Props = {
  isPending: boolean;
  /** Members see "Copy from My Account" / "Import from Profile"; admins editing someone else's listing don't. */
  showCopyFromAccount: boolean;
  profileFullName: string;
  profileBio: string;
  profileAvatar: string;
  copyFullName: boolean;
  copyBio: boolean;
  disableCopyFullName: boolean;
  disableCopyBio: boolean;
  onCopyFullNameChange: (checked: boolean) => void;
  onCopyBioChange: (checked: boolean) => void;
};

export const ProDirectoryStepPersonal: FC<Props> = ({
  isPending,
  showCopyFromAccount,
  profileFullName,
  profileBio,
  profileAvatar,
  copyFullName,
  copyBio,
  disableCopyFullName,
  disableCopyBio,
  onCopyFullNameChange,
  onCopyBioChange,
}) => {
  const { control, setValue } = useFormContext<TProFormValues>();
  const watchedProfession = useWatch({ control, name: 'profession' });
  const showOtherProfession = isOtherProfessionSelected(watchedProfession);

  return (
    <Grid container spacing={3}>
      {/* First half: Two image fields side by side */}
      <Grid item xs={12} md={6}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" sx={{ mb: 1 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">RYC® Pro Directory Image</Typography>
              <RequiredChip />
              <Tooltip title='This image will appear on the main "RYC® Pro Directory" page. The image should be a square at least 200 X 200. This image can be imported from your personal profile'>
                <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                  <MdHelpOutline size={16} />
                </Box>
              </Tooltip>
            </Stack>
            <Controller
              name="casualImage"
              control={control}
              render={({ field, fieldState }) => (
                <RhfMediaInlineUpload
                  value={field.value ?? undefined}
                  onChange={field.onChange}
                  processHandler={casualImageProcessHandler}
                  allowedFormats={FILE_FORMATS.IMAGE}
                  selectVariant={EImageVariant.w400}
                  uploadVariants={[EImageVariant.w400]}
                  cropAspectRatio={1}
                  width={200}
                  height={200}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            {showCopyFromAccount && (
              <Box mt={1}>
                <Tooltip title={!profileAvatar ? 'No image in your account profile' : ''}>
                  <Button
                    size="small"
                    sx={{ width: '200px' }}
                    variant="outlined"
                    startIcon={<MdDownload />}
                    disabled={!profileAvatar || isPending}
                    onClick={() => setValue('casualImage', profileAvatar, { shouldDirty: true })}
                  >
                    Import from Profile
                  </Button>
                </Tooltip>
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
              <Typography variant="subtitle2">RYC® Pro Directory Profile Image</Typography>
              <RequiredChip />
              <Tooltip title='This image will appear in your detailed "RYC® Pro Directory Profile" page. Image should be a rectangle at least 1375 X 950'>
                <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                  <MdHelpOutline size={16} />
                </Box>
              </Tooltip>
            </Stack>
            <Controller
              name="landscapeImage"
              control={control}
              render={({ field, fieldState }) => (
                <RhfMediaInlineUpload
                  value={field.value ?? undefined}
                  onChange={field.onChange}
                  processHandler={landscapeImageProcessHandler}
                  allowedFormats={FILE_FORMATS.IMAGE}
                  selectVariant={EImageVariant.w1375}
                  uploadVariants={[EImageVariant.w1375]}
                  cropAspectRatio={1375 / 950}
                  width={Math.round(200 * (1375 / 950))}
                  height={200}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Box>
        </Stack>
      </Grid>

      {/* Second half: Full name and title fields */}
      <Grid item xs={12} md={6}>
        <Stack spacing={2.5}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Full Name</Typography>
              <RequiredChip />
              {showCopyFromAccount && (
                <Tooltip title={!profileFullName ? 'No name in your account profile' : ''}>
                  <span>
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={copyFullName}
                          disabled={disableCopyFullName || isPending}
                          onChange={e => onCopyFullNameChange(e.target.checked)}
                        />
                      }
                      label={<Typography variant="caption">Copy from My Account</Typography>}
                    />
                  </span>
                </Tooltip>
              )}
            </Stack>
            <RHFTextField
              name="fullName"
              placeholder="Your full name"
              disabled={isPending || copyFullName}
              helperText="This will be your published name for the Pro Directory"
            />
          </Box>

          <Box>
            <Box sx={{ mb: 1, py: 0.4 }}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                Primary Professional Credential <RequiredChip />
              </Typography>
            </Box>
            <RHFTextField
              name="title"
              placeholder="e.g. Pelvic Floor Rehabilitation Specialist"
              disabled={isPending}
              helperText="Please write your official profession related to movement, Example: Pelvic Floor Rehabilitation Specialist"
            />
            <Typography
              sx={{
                ml: '14px',
                mt: '-4px',
                fontSize: '0.75rem',
                fontWeight: 400,
                color: 'text.secondary',
              }}
            >
              Choose one Primary Professional Credential. This is shown on your public profile as your title, right
              under your name.
            </Typography>
          </Box>
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          Certificates <OptionalChip />
        </Typography>
        <RHFTextField
          name="certificates"
          placeholder="List your certificates or qualifications"
          helperText="Please add your relevant certificates with a comma separating each certificate"
          disabled={isPending}
        />
      </Grid>

      {/* Profession */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          Professional (Credentials) <RequiredChip />
        </Typography>
        <RHFProfessionAutocomplete
          name="profession"
          multiple
          maxSelections={PROFESSION_MAX}
          disabled={isPending}
          helperText={`Please select up to ${PROFESSION_MAX} professions that apply to you`}
        />
      </Grid>

      {/* Only relevant once "Others" is picked in the profession list above. */}
      {showOtherProfession && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            Other Professions <OptionalChip />
          </Typography>
          <RHFTextField
            name="otherProfession"
            placeholder="Tell us which profession"
            disabled={isPending}
            helperText='Since you selected "Others" above, you can specify your profession here'
          />
        </Grid>
      )}

      {/* Professional Bio */}
      <Grid item xs={12}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Professional Bio</Typography>
          <RequiredChip />
          {showCopyFromAccount && (
            <Tooltip title={!profileBio ? 'No bio in your account profile' : ''}>
              <span>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={copyBio}
                      disabled={disableCopyBio || isPending}
                      onChange={e => onCopyBioChange(e.target.checked)}
                    />
                  }
                  label={<Typography variant="caption">Copy from My Account</Typography>}
                />
              </span>
            </Tooltip>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          This is your Professional Bio that will be published on your RYC® Pro Directory Profile page. You can copy the
          Bio from My Account Profile.
        </Typography>
        <Controller
          name="bio"
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <RichHtmlEditor
                value={field.value ?? ''}
                // An emptied editor emits `<p></p>`; store '' so required/dirty checks see it as empty.
                onChange={html => field.onChange(bioVisibleTextLength(html) === 0 ? '' : html)}
                placeholder="Tell members about your teaching practice"
                extensions={BIO_EXTENSIONS}
                allowedFormats={BIO_FORMATS}
                disabled={isPending || copyBio}
                style={fieldState.error ? { borderColor: 'error.main' } : undefined}
              />
              <Typography
                variant="caption"
                color={fieldState.error ? 'error' : 'text.secondary'}
                sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}
              >
                {fieldState.error?.message ?? `${bioVisibleTextLength(field.value)}/${BIO_MAX_VISIBLE_CHARS}`}
              </Typography>
            </Box>
          )}
        />
      </Grid>
    </Grid>
  );
};
